import { useId, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { auditionSampleKey, matchingAuditionRating } from "./auditionReviews";
import type { SoundAuditionsPayload, SoundVoice } from "./contracts";
import { SoundAuditionReview } from "./SoundAuditionReview";

export function SoundAuditionsPanel({
  auditions,
  voices,
  refreshing,
  onRefresh,
}: {
  auditions: SoundAuditionsPayload;
  voices: SoundVoice[];
  refreshing: boolean;
  onRefresh: () => void;
}) {
  const id = useId();
  const [search, setSearch] = useState("");
  const [profile, setProfile] = useState("");
  const [unreviewedOnly, setUnreviewedOnly] = useState(false);
  const manifest = auditions.latest_manifest;
  if (!manifest) return null;
  const names = new Map(
    voices.map((voice) => [
      voice.voice_id,
      voice.catalog?.display_name ?? voice.name,
    ]),
  );
  const rows = manifest.voices.map((sample) => ({
    sample,
    voiceName: names.get(sample.voice_id) ?? sample.voice_id,
    savedRating: matchingAuditionRating(auditions, sample),
  }));
  const filtered = rows.filter(
    ({ sample, voiceName, savedRating }) =>
      (!profile || sample.profile_id === profile) &&
      (!unreviewedOnly || !savedRating) &&
      `${voiceName} ${sample.voice_id}`
        .toLowerCase()
        .includes(search.toLowerCase()),
  );
  const available =
    auditions.status === "available" &&
    manifest.status === "available" &&
    !manifest.incomplete_reasons.length;

  return (
    <section
      aria-labelledby={`${id}-title`}
      className="space-y-3 rounded-lg border p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id={`${id}-title`} className="font-semibold">
            Audition reviews
          </h2>
          <p className="text-sm">
            Listen before rating. Check accent, natural delivery, complete
            words, and clear speech.
          </p>
          <p className="text-sm text-muted-foreground">
            {rows.filter((row) => row.savedRating).length} of {rows.length}{" "}
            samples reviewed · {manifest.pack_id}
          </p>
        </div>
        <Button
          variant="outline"
          type="button"
          disabled={refreshing}
          onClick={onRefresh}
        >
          Refresh samples
        </Button>
      </div>
      {!available ? (
        <p role="alert">
          This pack is incomplete or unavailable. Reviews cannot be saved.
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm" htmlFor={`${id}-search`}>
          Find a voice
        </label>
        <Input
          id={`${id}-search`}
          className="w-56"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <label className="text-sm" htmlFor={`${id}-profile`}>
          Profile
        </label>
        <select
          id={`${id}-profile`}
          className="h-9 rounded-md border bg-background px-2 text-sm"
          value={profile}
          onChange={(event) => setProfile(event.target.value)}
        >
          <option value="">All profiles</option>
          {manifest.profile_ids.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={unreviewedOnly}
            onChange={(event) => setUnreviewedOnly(event.target.checked)}
          />{" "}
          Unreviewed only
        </label>
      </div>
      <div className="max-h-[36rem] space-y-3 overflow-y-auto">
        {filtered.map(({ sample, voiceName, savedRating }) => (
          <SoundAuditionReview
            key={`${manifest.manifest_digest}:${auditionSampleKey(sample)}`}
            packId={manifest.pack_id}
            manifestDigest={manifest.manifest_digest}
            sample={sample}
            voiceName={voiceName}
            savedRating={savedRating}
            available={available}
          />
        ))}
        {!filtered.length ? (
          <p className="text-sm">No samples match these filters.</p>
        ) : null}
      </div>
    </section>
  );
}
